import { ReactNode } from 'react';
import { Loader2, RefreshCw, Save } from 'lucide-react';
import ActionButton from '../../../components/Buttons/ActionButton';
import ErrorAlertV2 from '../../../components/alerts-v2/ErrorAlertV2';
import SuccessAlertV2 from '../../../components/alerts-v2/SuccessAlertV2';
import { RestaurantSettingsAlert } from './RestaurantSettingsTypes';

interface Props {
  title: string;
  description: string;
  loading: boolean;
  saving: boolean;
  hasData: boolean;
  alert: RestaurantSettingsAlert | null;
  onCloseAlert: () => void;
  onSave: () => void;
  onRetry: () => void;
  children?: ReactNode;
  /** Botón de ayuda opcional que se muestra junto al título. */
  help?: ReactNode;
}

const RestaurantSettingsFormLayout = ({
  title,
  description,
  loading,
  saving,
  hasData,
  alert,
  onCloseAlert,
  onSave,
  onRetry,
  children,
  help,
}: Props) => (
  <section
    aria-labelledby="restaurant-settings-section-title"
    className="min-w-0 bg-white dark:bg-boxdark"
  >
    <div className="border-b border-stroke px-5 py-5 dark:border-strokedark sm:px-7">
      <div className="flex items-start justify-between gap-3">
        <h2
          id="restaurant-settings-section-title"
          className="text-xl font-semibold text-black dark:text-white"
        >
          {title}
        </h2>

        {help}
      </div>

      <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
        {description}
      </p>
    </div>

    <div className="px-5 py-6 sm:px-7">
      {alert?.type === 'success' && (
        <SuccessAlertV2
          isVisible
          title={alert.title}
          message={alert.message}
          onClose={onCloseAlert}
        />
      )}

      {alert?.type === 'error' && (
        <ErrorAlertV2
          isVisible
          title={alert.title}
          message={alert.message}
          onClose={onCloseAlert}
        />
      )}

      {loading ? (
        <div
          className="flex min-h-[280px] items-center justify-center gap-3 text-bodydark2 dark:text-bodydark"
          role="status"
        >
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span>Cargando configuración...</span>
        </div>
      ) : hasData ? (
        <>
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 xl:grid-cols-2">
            {children}
          </div>

          <div className="mt-6 flex justify-end border-t border-stroke pt-5 dark:border-strokedark">
            <ActionButton
              label={saving ? 'Guardando...' : 'Guardar cambios'}
              onClick={onSave}
              disabled={saving}
              icon={
                saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )
              }
              fullWidth={false}
              className="min-w-[180px]"
            />
          </div>
        </>
      ) : (
        <div className="flex min-h-[180px] items-center justify-center">
          <ActionButton
            label="Reintentar"
            onClick={onRetry}
            icon={<RefreshCw className="h-4 w-4" />}
            fullWidth={false}
          />
        </div>
      )}
    </div>
  </section>
);

export default RestaurantSettingsFormLayout;

